import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import SignUpForm from '../components/auth/SignUpForm';
import ResetPasswordForm from '../components/auth/ResetPasswordForm';
import EmailVerification from '../components/auth/EmailVerification';
import LanguageSelector from '../components/common/LanguageSelector';
import { Mail, Lock, Newspaper } from 'lucide-react';
import { motion } from 'framer-motion';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [view, setView] = useState<'login' | 'signup' | 'reset'>('login');
  const { login, isLoading, error, isEmailVerified, isAuthenticated } = useAuth();
  const { labels } = useLanguage();
  const navigate = useNavigate();

  // Handle navigation when auth state changes
  useEffect(() => {
    if (isAuthenticated && isEmailVerified) {
      navigate('/chat');
    }
  }, [isAuthenticated, isEmailVerified, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(email, password);
    // Navigation is now handled by useEffect
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-100 to-primary-200 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <motion.div
        className="sm:mx-auto sm:w-full sm:max-w-md"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-center">
          <div className="bg-primary-900 p-4 rounded-xl">
            <Newspaper className="h-16 w-16 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-primary-900">
          {labels.appTitle}
        </h2>
        <p className="mt-2 text-center text-sm text-primary-700 font-medium">
          {labels.appSubtitle}
        </p>
        <div className="mt-4 flex justify-center">
          <LanguageSelector />
        </div>
      </motion.div>

      <motion.div 
        className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-xl sm:px-10 border-2 border-primary-200">
          {view === 'login' && (
            <>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    label={labels.email}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    leftIcon={<Mail size={18} />}
                    fullWidth
                  />
                </div>
                <div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    label={labels.password}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    leftIcon={<Lock size={18} />}
                    fullWidth
                  />
                </div>
                {error && (
                  <div className="bg-error-50 text-error-700 p-3 rounded-md text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isLoading}
                    isFullWidth
                  >
                    {labels.login}
                  </Button>
                </div>
              </form>
              <div className="flex justify-between mt-4">
                <button
                  className="text-primary-600 hover:underline text-sm"
                  type="button"
                  onClick={() => setView('signup')}
                >
                  {labels.createAccount}
                </button>
                <button
                  className="text-primary-600 hover:underline text-sm"
                  type="button"
                  onClick={() => setView('reset')}
                >
                  {labels.forgotPassword}
                </button>
              </div>
            </>
          )}
          {view === 'signup' && (
            <>
              <SignUpForm />
              <div className="flex justify-between mt-4">
                <button
                  className="text-primary-600 hover:underline text-sm"
                  type="button"
                  onClick={() => setView('login')}
                >
                  {labels.alreadyHaveAccount}
                </button>
              </div>
            </>
          )}
          {view === 'reset' && (
            <>
              <ResetPasswordForm />
              <div className="flex justify-between mt-4">
                <button
                  className="text-primary-600 hover:underline text-sm"
                  type="button"
                  onClick={() => setView('login')}
                >
                  {labels.backToLogin}
                </button>
              </div>
            </>
          )}
          {isAuthenticated && !isEmailVerified && (
            <div className="mt-6">
              <EmailVerification />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;